<template lang="pug">
.dialog-container
  h4 {{ $t('Name-based media replacement in network.') }}
  hr.header
  span {{ $t('First letters to consider:') }} &nbsp;
  el-input-number(v-model='firstLetters', size='mini', :min='10', :max='100', @change='loadData()')
  el-button-group.float-right
    el-button(
      id="DialogReplaceByName-submitButton",
      size="mini",
      :disabled='isSaving || !someMatchableMediaSelected()'
      @click.stop='submitReplace()',
      type="success"
    )
      span {{ isSaving ? $t('replacing...') : $t('Replace') }}
  .warning(v-if='isRecalculating') {{ $t('recalculating...') }}
  .warning(id="DialogReplaceByName-duplicatesWarning", v-if='someMatchableMediaDuplicates') {{ $t('Multiple media are candidates for substitution of same medium. Probably something is wrong with media naming.') }}
  .container-dialog
    el-dialog(
      id="DialogReplaceByName-limitContainerDialog",
      v-if="mediumReplacementEdited !== undefined",
      :visible.sync="mediumReplacementEditedVisible",
      @close='mediumReplacementEdited = undefined; mediumReplacementEditedVisible = false; allPlayListsSelected = false',
      :close-on-click-modal="false",
      append-to-body
    )
      h4 {{ `${$t('Limit playlists/layouts in which')} '${mediumReplacementEdited.bestMatchedMedium.name}' ${$t('will be replaced. Leave blank to replace all occurrences in all playlists/layouts.')}` }}
      hr.header
      .row
        .column-left
          el-checkbox(id="DialogReplaceByName-limitSelectAllCheckbox", @change='toggleSelectAllPlaylists()', :value='allPlayListsSelected', :title='$t("Select/deselect all")', :label='$t("Select/deselect all")')
        .column-middle1
        .column-middle2
        .column-right
      .row(v-for='playlist of playlistsForReplacedMedium', :key='playlist.id', @click='togglePlaylistSelected(mediumReplacementEdited, playlist)')
        .column-left
          el-checkbox(:id="'DialogReplaceByName-limitSelect' + getMediumById(playlist.id).name", :value='playlistIsSelected(mediumReplacementEdited, playlist)', @change='togglePlaylistSelected(mediumReplacementEdited, playlist)')
        .column-middle1
          p {{ getMediumById(playlist.id).name }}
        .column-middle2
        .column-right
      .dirtyHack {{ dirtyHack }}
  .container-dialog
    .row
      .column-left
        el-checkbox(id="DialogReplaceByName-selectAllCheckbox", @change='toggleSelectAll()', :value='allMediaReplacementsSelected', :label='$t("Select/deselect all")')
      .column-middle1
      .column-middle2
      .column-right
        el-checkbox(id="DialogReplaceByName-currentPlaylistCheckbox", @change='toggleInCurrentPlaylist()', :value='inCurrentPlaylistSelected', :label='$t("Only in current playlist")')
    .medium-row(v-for='medium of mediaChosenSorted', :key='medium.id')
      .row-first(v-for='bestMatchedMedium of medium.bestMatchedMedia', :key='bestMatchedMedium.id', @click='toggleMediumSelected(bestMatchedMedium.id)')
        .column-left
          el-checkbox(:id="'DialogReplaceByName-replaceSelect' + getMediumById(medium.id).name", :value='bestMatchedMedium.selected', @change='toggleMediumSelected(bestMatchedMedium.id)')
        .column-middle1
          p
            img.thumbnail(:src='getMediumThumbnailPath(getMediumById(medium.id))')
            span.letters-considered {{ medium.name.substring(0, firstLetters) }}
            span {{ medium.name.substring(firstLetters) }}
          p.success
            font-awesome-icon(icon="arrow-down")
          p(:class="{ 'warning': mediaChosenSorted.filter(m => m.bestMatchedMedium.id === bestMatchedMedium.id).length > 1 }")
            img.thumbnail(:src='getMediumThumbnailPath(getMediumById(bestMatchedMedium.id))')
            span {{ bestMatchedMedium.name }}
        .column-middle2
          el-button(:id="'DialogReplaceByName-limitButton' + getMediumById(medium.id).name", v-if='!inCurrentPlaylistSelected', size="mini", @click.stop='editPlaylistsToReplaceMediumIn(medium, bestMatchedMedium)', type="success")
            span {{ bestMatchedMedium.playlists.length === 0 ? $t('Limit playlists') :  `${bestMatchedMedium.playlists.length} ${$t('playlists selected')}`}}
          p.playlists-list(v-for='pl of bestMatchedMedium.playlists.map(p => p.pathSplitted[0])', :key='pl') {{ pl }}
        .column-right
          el-progress(:text-inside="true", :stroke-width="20", :color='customColorMethod(parseInt(bestMatchedMedium.similarity*100))', :percentage="parseInt(bestMatchedMedium.similarity*100)")
      .row-second
    .dirtyHack {{ dirtyHack }}
  ConfirmDialog(
    v-if='confirmDialogVisible',
    :confirmDialogVisible='confirmDialogVisible',
    :question='confirmDialogQuestion',
    @confirmed='dialogConfirmed()',
    @closed='dialogDenied()'
  )
</template>

<script>
import Vue from 'vue'
import mediaUtils from '../Common/mediaUtils.mixin'
import levenshtein from 'damerau-levenshtein'
import ConfirmDialog from '../Common/ConfirmDialog'
export default {
  props: [],
  mixins: [ mediaUtils ],
  components: {
    ConfirmDialog
  },
  created () {
    this.loadData()
  },
  data () {
    return {
      errors: [],
      isSaving: false,
      isRecalculating: false,
      firstLetters: 25,
      mediaChosen: [],
      allMediaReplacementsSelected: false,
      allPlayListsSelected: false,
      inCurrentPlaylistSelected: false,
      playlistsForReplacedMedium: [],
      mediumReplacementEdited: undefined,
      mediumReplacementEditedVisible: false,
      confirmDialogVisible: false,
      confirmDialogQuestion: '',
      dirtyHack: false
    }
  },
  methods: {
    loadData () {
      this.isRecalculating = true
      this.mediaChosen = JSON.parse(JSON.stringify(this.$store.state.Medium.media_selected_left.filter(m => m.type.indexOf('PlayList') === -1)))
      for (const mediumSelected of this.mediaChosen) {
        this.__getClosestMatches(mediumSelected)
      }
      this.isRecalculating = false
    },
    __getClosestMatches (medium) {
      medium.bestMatchedMedium = { similarity: 0 }
      medium.bestMatchedMedia = []
      for (const mediumInStore of this.matchableMedia) {
        const lev = levenshtein(medium.name.substring(0, this.firstLetters), mediumInStore.name.substring(0, this.firstLetters))
        if (medium.bestMatchedMedium.similarity <= lev.similarity && medium.id !== mediumInStore.id) {
          medium.bestMatchedMedium = {
            id: mediumInStore.id,
            similarity: lev.similarity,
            name: mediumInStore.name,
            type: mediumInStore.type,
            playlists: []
          }
          medium.bestMatchedMedia.push(medium.bestMatchedMedium)
          medium.bestMatchedMedia = medium.bestMatchedMedia.filter(m => m.similarity === lev.similarity)
        }
      }
    },

    toggleSelectAllPlaylists () {
      this.allPlayListsSelected = !this.allPlayListsSelected
      if (!this.allPlayListsSelected) {
        this.mediumReplacementEdited.matchedMediumPlaylistSelection.playlists = []
      } else {
        this.mediumReplacementEdited.matchedMediumPlaylistSelection.playlists = JSON.parse(JSON.stringify(this.playlistsForReplacedMedium))
      }
    },
    toggleInCurrentPlaylist () {
      this.inCurrentPlaylistSelected = !this.inCurrentPlaylistSelected
      this.loadData()
      if (this.inCurrentPlaylistSelected) {
        for (const medium of this.mediaChosenSorted) {
          console.log(this.currentPlaylistOpenInEditor)
          medium.matchedMediumPlaylistSelection.playlists = [this.currentPlaylistOpenInEditor]
        }
      }
    },
    playlistIsSelected  (mediumReplacementEdited, playlist) {
      return mediumReplacementEdited.matchedMediumPlaylistSelection.playlists.findIndex(pl => pl.id === playlist.id) > -1
    },
    togglePlaylistSelected (mediumReplacementEdited, playlist) {
      if (mediumReplacementEdited.matchedMediumPlaylistSelection.playlists.length === 0) {
        mediumReplacementEdited.matchedMediumPlaylistSelection.playlists.push(playlist)
      } else {
        const playlistIndex = mediumReplacementEdited.matchedMediumPlaylistSelection.playlists.findIndex(pl => pl.id === playlist.id)
        if (playlistIndex > -1) {
          mediumReplacementEdited.matchedMediumPlaylistSelection.playlists.splice(playlistIndex, 1)
        } else {
          mediumReplacementEdited.matchedMediumPlaylistSelection.playlists.push(playlist)
        }
      }
      console.log(mediumReplacementEdited.matchedMediumPlaylistSelection.playlists)
      this.dirtyHack = !this.dirtyHack
    },
    async editPlaylistsToReplaceMediumIn (medium, bestMatchedMedium) {
      this.mediumReplacementEdited = medium
      this.mediumReplacementEdited.matchedMediumPlaylistSelection = bestMatchedMedium
      this.playlistsForReplacedMedium = (await Vue.axios.post('medium/findPlaylistsAffectedFullPath', { 'Medium': bestMatchedMedium.id }))
        .data.occurrences
        // replace only in media playlists (not library folders)
        .filter(pl => pl.type === 'PlayList' || pl.type === 'PlayListLayout' || pl.type === 'PlayListExtended')
        // // only direct playitems
        .filter(pl => pl.level === 1)
      this.mediumReplacementEditedVisible = true
    },
    toggleSelectAll () {
      this.allMediaReplacementsSelected = !this.allMediaReplacementsSelected
      for (const medium of this.mediaChosen) {
        for (const bmmedium of medium.bestMatchedMedia) {
          bmmedium.selected = this.allMediaReplacementsSelected
        }
      }
    },
    toggleMediumSelected (mediumId) {
      this.dirtyHack = !this.dirtyHack
      for (const medium of this.mediaChosen) {
        for (const bestMatchedMedium of medium.bestMatchedMedia) {
          if (mediumId === bestMatchedMedium.id) bestMatchedMedium.selected = !bestMatchedMedium.selected
        }
      }
    },
    customColorMethod (percentage) {
      if (percentage < 30) {
        return '#a72423'
      } else if (percentage < 70) {
        return '#824c00'
      } else {
        return '#006900'
      }
    },
    submitReplace () {
      this.confirmDialogQuestion = this.$t(`Are you sure you want to make replacents?`)
      this.confirmDialogVisible = true
    },
    async dialogConfirmed () {
      this.confirmDialogVisible = false
      this.isSaving = true
      for (const mediumChosen of this.mediaChosen) {
        for (const bestMatchedMedium of mediumChosen.bestMatchedMedia) {
          if (!bestMatchedMedium.selected) continue
          mediumChosen.bestMatchedMedium = bestMatchedMedium
          await Vue.axios.post('medium/replaceMediaByName', { mediaChosen: [mediumChosen] })
        }
      }
      this.isSaving = false
      this.$emit('linkerJobDone')
    },
    dialogDenied () {
      this.confirmDialogVisible = false
    },
    someMatchableMediaSelected () {
      const bestMatchedSelectedArray = this.mediaChosen.filter(mc => mc.bestMatchedMedia.find(bmm => bmm.selected))
      return bestMatchedSelectedArray.length > 0
    }
  },
  computed: {
    currentPlaylistMediaIds () {
      const playlistEdited = this.$store.state.Medium.media_list.find(m => m.id === this.$store.state.Medium.breadcrumbs[this.$store.state.Medium.breadcrumbs.length - 1])
      return playlistEdited.PlayListItems.map(pli => pli.Medium)
    },
    currentPlaylistOpenInEditor () {
      return this.$store.state.Medium.media_list.find(m => m.id === this.$store.state.Medium.breadcrumbs[this.$store.state.Medium.breadcrumbs.length - 1])
    },
    matchableMedia () {
      // do not include library playlists
      const media = this.$store.state.Medium.media_list.filter(m => m.type.indexOf('PlayList') === -1)
      // do not include unassigned media
      const unassignedMediaIds = this.$store.state.Medium.media_types.find(t => t.name === 'UnassignedMedia').media
      let result = media.filter(m => !unassignedMediaIds.includes(m.id))
      return !this.inCurrentPlaylistSelected ? result : result.filter(m => this.currentPlaylistMediaIds.includes(m.id))
    },
    someMatchableMediaDuplicates () {
      const bestMatchedArray = this.mediaChosen.map(mc => mc.bestMatchedMedium.id)
      return (new Set(bestMatchedArray)).size < bestMatchedArray.length
    },
    mediaChosenSorted () {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return this.mediaChosen
        .sort((a, b) => parseFloat(b.bestMatchedMedium.similarity) - parseFloat(a.bestMatchedMedium.similarity))
    }
  }
}
</script>
<style lang="scss" scoped>
@import "../../../theme-m4b/src/common/var.scss";
.thumbnail {
  max-width: 50px;
  max-height: 50px;
  margin-right: 10px;
}
.row, .row-first, .row-second {
  display: flex;
  align-items: center;
  border-bottom: $--border-base;
  .column-left {
    width: 10%;
  }
  .column-middle1 {
    width: 40%;
    word-break: break-all;
  }
  .column-middle2 {
    width: 20%;
  }
  .column-right {
    width: 30%;
  }
  .playlists-list {
    word-break: break-all;
  }
}
.dirtyHack {
  opacity: 0;
}
.letters-considered {
  color: $--color-success;
}
.dialog-container {
  font-size: $--font-size-base;
}
</style>



// WEBPACK FOOTER //
// src/components/Linker/DialogReplaceByName.vue